import * as z from 'zod'

/**
 * The zip format this build reads and writes (spec §3.1). An importer that meets a
 * different value refuses by name rather than half-importing, so the schema parses the
 * number and the comparison is the importer's — not zod's.
 */
export const SPACE_EXPORT_FORMAT_VERSION = 1

/**
 * The kernel schema `tunasong.db` was written against (spec §3.1). Recorded so L6's
 * upgrade path can migrate an imported database exactly as it migrates a long-lived
 * local one — no second mechanism.
 */
export const KERNEL_SCHEMA_VERSION = 2

export const spaceExportMissingSchema = z.object({
  entityId: z.string(),
  filename: z.string(),
  reason: z.string(),
})
export type SpaceExportMissing = z.infer<typeof spaceExportMissingSchema>

/**
 * THE definition of `counts.bytes`, which both writers of this format implement and the
 * import preflight reads. It is named here, once, because two writers of one format is
 * exactly how a field drifts: `backend/local-host/src/export-library.ts` (the kernel
 * packaging its own data directory) and `backend/workflows/src/workflows/export/export-space.ts`
 * (the cloud exporting a subtree) both point at this comment rather than each inventing a
 * rule.
 *
 * The total is the on-disk size of the files an IMPORT would copy: every document under
 * `docs/` plus every media blob under `storage/`, derived files included. It deliberately
 * excludes `tunasong.db` and `manifest.json` — an import reads both and copies neither —
 * and it is a sum of file sizes, never the compressed size of the zip.
 *
 * Optional, and the format version is deliberately NOT bumped for it: `import-space.ts`
 * compares `formatVersion` with `!==`, so a bump would make every shipped build refuse
 * every new zip and this build refuse every zip written before today. An additive optional
 * field costs neither — an older zip parses and simply carries no total.
 */
export const spaceExportCountsSchema = z.object({
  entities: z.number().int().nonnegative(),
  edges: z.number().int().nonnegative(),
  docs: z.number().int().nonnegative(),
  blobs: z.number().int().nonnegative(),
  bytes: z
    .number()
    .int()
    .nonnegative()
    .describe('Total size on disk of the documents and media blobs an import would copy — see this schema’s comment')
    .optional(),
})

export const spaceExportManifestSchema = z.object({
  formatVersion: z.number().int(),
  kind: z.literal('space'),
  exportedAt: z.iso.datetime(),
  /** The exporting user. The kernel adopts this identity on import (spec §5.2) */
  sourceUserId: z.string(),
  rootEntityId: z.string(),
  counts: spaceExportCountsSchema,
  kernelSchemaVersion: z.number().int(),
  missing: z
    .array(spaceExportMissingSchema)
    .describe('Files an entity references that could not be fetched at export time (spec §7)')
    .optional(),
})
export type SpaceExportManifest = z.infer<typeof spaceExportManifestSchema>

/**
 * What an import wrote into the uploader's space (front-door spec §5.2), at
 * `users/<uid>/workflows/import-space/<jobId>.report.json`: the import task writes it, and
 * the status lambda reads it back for the Profile → Imports page. Both parse through here.
 */
export const importSpaceReportSchema = z.object({
  /** The one folder this import made; every upload root is in it (spec §5.1) */
  folderId: z.string(),
  entities: z.number().int().nonnegative(),
  edges: z.number().int().nonnegative(),
  /** Media files copied */
  blobs: z.number().int().nonnegative(),
  skipped: z.object({
    /** Rows that stay local on purpose: profiles */
    localOnly: z.number().int().nonnegative(),
    /** Rows dropped because a reference they require sat outside the upload */
    unresolvedReference: z.number().int().nonnegative(),
  }),
  /** Imported media items whose file was not in the upload, by their NEW id */
  missing: z.array(z.object({ entityId: z.string(), name: z.string(), type: z.string() })),
})
export type ImportSpaceReport = z.infer<typeof importSpaceReportSchema>

/**
 * The report's first write, right after the folder is made and before any entity (spec §6.1
 * consequence 1), so a run that fails partway can still say where its partial import is. The
 * full report overwrites it; read through this schema, a full report yields its folder too.
 */
export const importSpaceReportStubSchema = importSpaceReportSchema.pick({ folderId: true })
export type ImportSpaceReportStub = z.infer<typeof importSpaceReportStubSchema>
